import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UserPlus, Clock, X, Send, CalendarPlus } from 'lucide-react';
import api from '../utils/api';

const ago = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const NewRegistrationsBox = ({ onBook, refreshKey, compact = false }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/registrations/pending');
      setItems(res.data || []);
    } catch (e) { /* silent — box is auxiliary */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, refreshKey]);

  const dismiss = async (inv) => {
    setBusy(inv.id);
    try {
      await api.post(`/registrations/${inv.id}/dismiss`);
      setItems((prev) => prev.filter((i) => i.id !== inv.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not dismiss');
    } finally { setBusy(null); }
  };

  const resend = async (inv) => {
    setBusy(inv.id);
    try {
      const res = await api.post('/registrations/invite', { phone: inv.phone, country_code: inv.country_code });
      if (res.data?.wa_success === false) toast.error(`WhatsApp send failed: ${res.data.wa_error || 'unknown error'}`);
      else toast.success(`Link re-sent to ${inv.phone}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally { setBusy(null); }
  };

  if (!items.length) return null;
  const registered = items.filter((i) => i.status === 'registered' && i.patient);
  const awaiting = items.filter((i) => i.status === 'sent');

  return (
    <div className={`${compact ? 'mx-2 mt-2' : 'mt-6'} p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl`} data-testid="new-registrations-box">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> New Registrations
        </h3>
        <span className="text-xs font-semibold text-emerald-700 bg-white/70 px-2 py-0.5 rounded-full" data-testid="new-registrations-count">
          {registered.length} ready{awaiting.length ? ` · ${awaiting.length} awaiting` : ''}
        </span>
      </div>

      <div className="space-y-2">
        {registered.map((inv) => (
          <div key={inv.id} className="group flex items-center gap-1 bg-white rounded-lg pl-3 pr-1 py-2 shadow-sm border border-emerald-100" data-testid={`registration-item-${inv.id}`}>
            <button
              onClick={() => onBook(inv.patient)}
              className="flex-1 text-left min-w-0"
              data-testid={`registration-book-${inv.id}`}
              title="Create appointment"
            >
              <div className="font-semibold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">{inv.patient.name}</div>
              <div className="text-xs text-gray-500 truncate">
                {inv.phone}{inv.patient.gender ? ` · ${inv.patient.gender}` : ''}{inv.patient.age ? ` · ${inv.patient.age} yrs` : (inv.patient.dob ? ` · ${inv.patient.dob}` : '')}
              </div>
              <div className="text-[11px] text-emerald-600">registered {ago(inv.registered_at)}</div>
            </button>
            <button onClick={() => onBook(inv.patient)} className="p-1.5 rounded-md text-emerald-700 hover:bg-emerald-100 transition-colors" title="Create appointment" data-testid={`registration-book-icon-${inv.id}`}>
              <CalendarPlus className="w-4 h-4" />
            </button>
            <button onClick={() => dismiss(inv)} disabled={busy === inv.id} className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Dismiss" data-testid={`registration-dismiss-${inv.id}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {awaiting.map((inv) => (
          <div key={inv.id} className="flex items-center gap-1 bg-white/60 rounded-lg pl-3 pr-1 py-2 border border-dashed border-amber-200" data-testid={`registration-awaiting-${inv.id}`}>
            <Clock className="w-4 h-4 text-amber-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-800 font-medium">{inv.phone}</div>
              <div className="text-[11px] text-gray-500 truncate">
                sent {ago(inv.sent_at)} by {inv.sent_by_name}{inv.resend_count ? ` · resent ×${inv.resend_count}` : ''}
              </div>
              {inv.wa_success === false && <div className="text-[11px] text-red-600 truncate" title={inv.wa_error || ''}>⚠ WhatsApp send failed</div>}
            </div>
            <button onClick={() => resend(inv)} disabled={busy === inv.id} className="p-1.5 rounded-md text-amber-700 hover:bg-amber-100 transition-colors" title="Resend link" data-testid={`registration-resend-${inv.id}`}>
              <Send className="w-4 h-4" />
            </button>
            <button onClick={() => dismiss(inv)} disabled={busy === inv.id} className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Dismiss" data-testid={`registration-dismiss-${inv.id}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewRegistrationsBox;
